import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, Checkbox, FormField, Input, Label, SelectField, type FormFieldControlProps } from "@lendcord/concord";
import { PasswordInput } from "@/auth/components/PasswordInput";
import { TermsDialog } from "@/auth/components/TermsDialog";
import { isQueryApiError } from "@/app/store/baseApi";
import type { CredentialsFormValues } from "@/auth/auth.types";
import { EMAIL_REGEX, NAME_REGEX, PASSWORD_REGEX, PHONE_REGEX } from "@/auth/validators";

// Privacy Policy is authored once in apps/marketing (constants/common.ts's
// PRIVACY_POLICY) and published there — linked out to rather than mirrored
// into a second copy here, so there's one source of truth to keep current.
const MARKETING_URL = process.env.MARKETING_URL || "/";

interface CredentialsFormProps {
  onSubmit: (values: CredentialsFormValues) => Promise<void> | void;
}

type PhoneInputProps = FormFieldControlProps & {
  value: string;
  onChange: (value: string) => void;
};

// FormField clones its single child and injects id/aria/state props onto it —
// this wrapper forwards those onto the real <input> so the +91 prefix chip
// doesn't break the label[for] <-> input[id] association.
function PhoneInput({ value, onChange, ...controlProps }: PhoneInputProps) {
  const intl = useIntl();
  return (
    <div className="flex gap-2">
      <span
        className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted"
        aria-hidden="true"
      >
        +91
      </span>
      <Input
        {...controlProps}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 10))}
        inputMode="numeric"
        placeholder={intl.formatMessage({ id: "auth.credentialsForm.phonePlaceholder" })}
        autoComplete="tel-national"
        className="flex-1"
      />
    </div>
  );
}

// LEND-002 deliberately covers both "email taken" and "phone taken" without
// distinguishing which, to avoid leaking account existence to an
// unauthenticated caller — see the Postman collection's top-level notes.
function signupFormError(intl: ReturnType<typeof useIntl>, error: unknown): string {
  if (isQueryApiError(error) && error.code === "LEND-002") {
    return intl.formatMessage({ id: "auth.credentialsForm.duplicateAccountError" });
  }
  return intl.formatMessage({ id: "auth.credentialsForm.genericSubmitError" });
}

export function CredentialsForm({ onSubmit }: CredentialsFormProps) {
  const intl = useIntl();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
    role?: string;
    consentGiven?: string;
  }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [hasViewedTerms, setHasViewedTerms] = useState(false);

  function handleOpenTerms() {
    setTermsDialogOpen(true);
    setHasViewedTerms(true);
  }

  function validate(): boolean {
    const nextErrors: typeof errors = {};
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst || !NAME_REGEX.test(trimmedFirst)) {
      nextErrors.firstName = intl.formatMessage({ id: "auth.credentialsForm.firstNameError" });
    }
    if (!trimmedLast || !NAME_REGEX.test(trimmedLast)) {
      nextErrors.lastName = intl.formatMessage({ id: "auth.credentialsForm.lastNameError" });
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      nextErrors.email = intl.formatMessage({ id: "auth.credentialsForm.emailError" });
    }
    if (!PASSWORD_REGEX.test(password)) {
      nextErrors.password = intl.formatMessage({ id: "auth.credentialsForm.passwordError" });
    }
    if (confirmPassword !== password) {
      nextErrors.confirmPassword = intl.formatMessage({ id: "auth.credentialsForm.confirmPasswordError" });
    }
    if (!PHONE_REGEX.test(phone)) {
      nextErrors.phone = intl.formatMessage({ id: "auth.credentialsForm.phoneError" });
    }
    if (!role) {
      nextErrors.role = intl.formatMessage({ id: "auth.credentialsForm.roleError" });
    }
    if (!consentGiven) {
      nextErrors.consentGiven = intl.formatMessage({ id: "auth.credentialsForm.consentError" });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        phone,
        role,
        consentGiven,
      });
    } catch (error) {
      setFormError(signupFormError(intl, error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
      {formError && <AlertBanner status="error">{formError}</AlertBanner>}

      <div className="grid grid-cols-2 gap-3">
        <FormField
          label={intl.formatMessage({ id: "auth.credentialsForm.firstNameLabel" })}
          error={errors.firstName}
          required
        >
          <Input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder={intl.formatMessage({ id: "auth.credentialsForm.firstNamePlaceholder" })}
            autoComplete="given-name"
          />
        </FormField>

        <FormField
          label={intl.formatMessage({ id: "auth.credentialsForm.lastNameLabel" })}
          error={errors.lastName}
          required
        >
          <Input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            placeholder={intl.formatMessage({ id: "auth.credentialsForm.lastNamePlaceholder" })}
            autoComplete="family-name"
          />
        </FormField>
      </div>

      <SelectField
        label={intl.formatMessage({ id: "auth.credentialsForm.roleLabel" })}
        placeholder={intl.formatMessage({ id: "auth.credentialsForm.rolePlaceholder" })}
        value={role}
        onValueChange={setRole}
        error={errors.role}
        required
        options={[
          { value: "BORROWER", label: intl.formatMessage({ id: "auth.credentialsForm.roleBorrower" }) },
          { value: "LENDER", label: intl.formatMessage({ id: "auth.credentialsForm.roleLender" }) },
        ]}
      />

      <FormField
        label={intl.formatMessage({ id: "auth.credentialsForm.emailLabel" })}
        error={errors.email}
        required
      >
        <Input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={intl.formatMessage({ id: "auth.credentialsForm.emailPlaceholder" })}
          autoComplete="email"
        />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.credentialsForm.mobileNumberLabel" })}
        error={errors.phone}
        required
        hint={
          errors.phone
            ? undefined
            : intl.formatMessage({ id: "auth.credentialsForm.mobileNumberHint" })
        }
      >
        <PhoneInput value={phone} onChange={setPhone} />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.credentialsForm.passwordLabel" })}
        error={errors.password}
        required
        hint={
          errors.password
            ? undefined
            : intl.formatMessage({ id: "auth.credentialsForm.passwordHint" })
        }
      >
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder={intl.formatMessage({ id: "auth.credentialsForm.passwordPlaceholder" })}
          autoComplete="new-password"
        />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.credentialsForm.confirmPasswordLabel" })}
        error={errors.confirmPassword}
        required
      >
        <PasswordInput
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder={intl.formatMessage({ id: "auth.credentialsForm.confirmPasswordPlaceholder" })}
          autoComplete="new-password"
          disablePaste
        />
      </FormField>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Checkbox
            id="consentGiven"
            checked={consentGiven}
            disabled={!hasViewedTerms}
            onCheckedChange={(checked) => setConsentGiven(checked === true)}
          />
          <Label htmlFor="consentGiven">
            <FormattedMessage id="auth.credentialsForm.consentLabelPrefix" />{" "}
            <button
              type="button"
              onClick={handleOpenTerms}
              className="font-medium text-accent-600 hover:underline"
            >
              <FormattedMessage id="auth.credentialsForm.consentLabelLink" />
            </button>{" "}
            <FormattedMessage id="auth.credentialsForm.consentLabelAnd" />{" "}
            <a
              href={`${MARKETING_URL}/legal/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent-600 hover:underline"
            >
              <FormattedMessage id="auth.credentialsForm.consentLabelSuffix" />
            </a>
          </Label>
        </div>
        {errors.consentGiven ? (
          <p className="text-xs text-error-fg">{errors.consentGiven}</p>
        ) : (
          !hasViewedTerms && (
            <p className="text-xs text-text-muted">
              <FormattedMessage id="auth.credentialsForm.consentViewRequiredHint" />
            </p>
          )
        )}
      </div>

      <TermsDialog open={termsDialogOpen} onOpenChange={setTermsDialogOpen} />

      <Button type="submit" disabled={isSubmitting || !consentGiven}>
        {isSubmitting ? (
          <FormattedMessage id="auth.credentialsForm.submittingButton" />
        ) : (
          <FormattedMessage id="auth.credentialsForm.submitLabelDefault" />
        )}
      </Button>
    </form>
  );
}
